import {
  type BadgeProps,
  CheckIcon,
  DraftIcon,
  ErrorIcon,
  FolderIcon,
  MeetingIcon,
  PauseIcon,
  PlayIcon,
  RefreshIcon,
} from "@hightouch/htui";

import {
  DerivedCampaignStatus,
  type GetCampaignsQuery,
  type GetCampaignAllStatsQuery,
  type GetMessagesQuery,
} from "src/graphql";

import type { CepBrand } from "src/cep/brands/use-cep-brands";
import type { RenderedOutput, TriggerInfo } from "src/cep/messages/types";
import type { ChannelType } from "src/types/cep";
import { HourglassIcon } from "src/ui/icons";
import type { MarkerText } from "src/cep/messages/message/message-composer/dynamic-content/dynamic-content-types";
export type UnifiedRow<C, M> =
  | { kind: "campaign"; id: string; data: C }
  | { kind: "message"; id: string; data: M };

export const DerivedStatusToBadgeProps: Record<
  DerivedCampaignStatus,
  Pick<BadgeProps, "variant" | "isPending" | "svgIcon" | "progress">
> = {
  [DerivedCampaignStatus.Draft]: { variant: "ghost", isPending: true },
  [DerivedCampaignStatus.Scheduled]: {
    variant: "info",
    isPending: true,
    svgIcon: MeetingIcon,
  },
  [DerivedCampaignStatus.Activated]: { variant: "info", isPending: true },
  // TODO: add real progress once that's available
  [DerivedCampaignStatus.Running]: {
    variant: "info",
    progress: "indeterminate",
  },
  [DerivedCampaignStatus.Paused]: { variant: "warning" },
  [DerivedCampaignStatus.Completed]: { variant: "success" },
  [DerivedCampaignStatus.Archived]: { variant: "subtle" },
  [DerivedCampaignStatus.Error]: { variant: "error", svgIcon: ErrorIcon },
};

export const DerivedStatusToIconMap: Record<
  DerivedCampaignStatus,
  typeof DraftIcon
> = {
  [DerivedCampaignStatus.Draft]: DraftIcon,
  [DerivedCampaignStatus.Scheduled]: MeetingIcon,
  [DerivedCampaignStatus.Activated]: RefreshIcon,
  [DerivedCampaignStatus.Running]: PlayIcon,
  [DerivedCampaignStatus.Paused]: PauseIcon,
  [DerivedCampaignStatus.Completed]: CheckIcon,
  [DerivedCampaignStatus.Archived]: FolderIcon,
  [DerivedCampaignStatus.Error]: ErrorIcon,
};

export const DerivedStatusToLabelMap: Record<DerivedCampaignStatus, string> = {
  [DerivedCampaignStatus.Draft]: "Draft",
  [DerivedCampaignStatus.Scheduled]: "Scheduled",
  [DerivedCampaignStatus.Activated]: "Active",
  [DerivedCampaignStatus.Running]: "In progress",
  [DerivedCampaignStatus.Paused]: "Paused",
  [DerivedCampaignStatus.Completed]: "Completed",
  [DerivedCampaignStatus.Archived]: "Archived",
  [DerivedCampaignStatus.Error]: "Error",
};

/**
 * A campaign's staged draft, tracked separately from the derived status because
 * a draft can sit against a campaign in any non-terminal status.
 */
export enum CampaignDraftState {
  InProgress = "in_progress",
  PendingApproval = "pending_approval",
}

export const DraftStateToBadgeProps: Record<
  CampaignDraftState,
  Required<Pick<BadgeProps, "variant" | "isPending" | "svgIcon">>
> = {
  [CampaignDraftState.InProgress]: {
    variant: "ghost",
    isPending: true,
    svgIcon: DraftIcon,
  },
  [CampaignDraftState.PendingApproval]: {
    variant: "accent",
    isPending: true,
    svgIcon: HourglassIcon,
  },
};

export const DraftStateToLabelMap: Record<CampaignDraftState, string> = {
  [CampaignDraftState.InProgress]: "In progress",
  [CampaignDraftState.PendingApproval]: "Pending approval",
};

export type CampaignDetailState = {
  autoFocusName?: boolean;
};

export type Campaign = GetCampaignsQuery["campaigns"][number];
export type CampaignAllTimeStats =
  GetCampaignAllStatsQuery["getCampaignAllStats"]["allTime"][number];
export type CampaignHourlyEntry =
  GetCampaignAllStatsQuery["getCampaignAllStats"]["hourly"][number];

export type CampaignWithMetrics = Campaign & {
  dynamicContentLabels?: ReadonlyMap<string, string>;
  markerText?: MarkerText;
  channels: ChannelType[];
  /** The brand behind this campaign's messages. */
  brand: CepBrand | null;
  derivedStatus?: DerivedCampaignStatus;
  subtitle?: string;
  /** Not `subtitle`, which falls back to a push title. */
  subject?: string;
  preheader?: string;
  renderedOutput?: RenderedOutput;
};

// The unified campaigns list (cepLabels) interleaves campaign rows with
// journey-triggered message rows. A message row is one GetMessages row enriched
// with the same preview/derived fields a campaign row carries, plus its journey
// trigger (name + derived state) for the Schedule and Status cells.
export type MessageRow = GetMessagesQuery["messages"][number];

export type MessageWithMetrics = MessageRow & {
  channel?: ChannelType;
  dynamicContentLabels?: ReadonlyMap<string, string>;
  markerText?: MarkerText;
  trigger: TriggerInfo | null;
  brand: CepBrand | null;
  subject?: string;
  preheader?: string;
  renderedOutput?: RenderedOutput;
};

// One row of the unified list, tagged by source so each table cell renders the
// right shape. Flag-off, every row is `kind: "campaign"`.
export type UnifiedListRow = UnifiedRow<
  CampaignWithMetrics,
  MessageWithMetrics
>;
